/** This is a generic search tile which are used on condensed state of
 *  Flash, Last Minute and Tactical Always On as the designs are same */

import React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import cn from 'clsx'

import { mediaQueryUp } from 'components/utils/breakpoint'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import shouldHideAoLabels from 'lib/offer/hideAoLabel'
import { OFFER_TYPE_LAST_MINUTE } from 'constants/offer'
import OfferBadge from 'components/Common/OfferBadge'
import SoldOutMessage from '../Support/SoldOutMessage'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import OfferUrgencyLabels from 'components/Common/Labels/OfferUrgencyLabels'
import FloatingBookmarkContainer from 'tripPlanner/components/Bookmark/Common/FloatingBookmarkContainer'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { showUserReviewsRating } from 'lib/order/reviewUtils'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import { encodeOfferIds } from 'components/Pages/HotelSearchPage/HotelSearchMapPage'
import { queryKeySelectedOfferIds } from 'constants/url'
import { setSearchParamValue } from 'lib/url/searchUrlUtils'
import { connect } from 'react-redux'
import { getShowOnMapOfferExperimentVariant } from 'selectors/searchSelectors'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferTileLocationSection from './OfferTileLocationSection'
import ResponsiveImage from 'components/Common/ResponsiveImage/ResponsiveImage'
import { OptimizelyClientSideExperiments, useOptimizelyExperiment } from 'lib/optimizely/optimizelyUtils'
import Breakpoint from 'components/Common/Breakpoint'

const Main = styled.div`
  display: flex;
  flex-direction:column;

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
  ${mediaQueryUp.desktop} {
    display: grid;
    grid-template-columns: 40% 1fr;
  }
`

const MainImageSegment = styled.div`
  display: grid;
  position: relative;
  overflow: hidden;

  ${mediaQueryUp.desktop} {
    grid-template-rows: 1fr;
    max-height: ${rem(200)};
  }
`

const Content = styled.div`
  padding: ${rem(16)};
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${mediaQueryUp.desktop} {
    flex-direction: row;
    max-height: ${rem(262)};
    padding: ${rem(24)} ${rem(24)} ${rem(16)}
  }
`

const LeftContent = styled.div`
  align-self: flex-start;
  flex: 1;
`

const RightContent = styled.div`
  align-self: flex-end;
  margin-top: ${rem(8)};
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  ${mediaQueryUp.tablet} {
    margin-left: ${rem(8)};
  }
`

const StyledBodyTextBlock = styled(BodyTextBlock)`
  margin: ${rem(16)} 0;

  ${mediaQueryUp.tablet} {
    margin: ${rem(4)} 0;
  }
`

const ProductTypeLabelContainer = styled.div`
  position: absolute;
  top: ${rem(20)};
  left: ${rem(20)};
`

const OfferOverlayContainer = styled.div`
  position: absolute;
  top: ${rem(54)};
  left: ${rem(20)};
  display: flex;
  align-items: flex-start;

  &.hideTypeLabel {
    top: ${rem(20)};
  }

  & > * + * {
    margin-top: ${rem(12)};
  }
`

const StyledOfferBadge = styled(OfferBadge)`
  position: absolute;
  top: ${rem(16)};
  right: ${rem(16)};
`

interface Props {
  offer: App.Offer | App.OfferSummary;
  productClick?: (offer: App.Offer | App.OfferSummary) => void;
  filters?: App.OfferListFilters;
  eagerLoadFirstImage?: boolean;
  windowSearch: string;
  shouldShowMapButton: boolean;
}

function SearchOfferTileCondensed(props: Props) {
  const {
    offer,
    filters,
    windowSearch,
    shouldShowMapButton,
  } = props

  const {
    property,
    name,
    type,
    badge,
  } = offer

  const location = [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', ')
  const hideAoLabel = shouldHideAoLabels(offer)

  const mapSearchUrl = `/search/map?${setSearchParamValue(windowSearch, queryKeySelectedOfferIds, encodeOfferIds([offer]))}`
  const isSaveButtonExperimentEnabled = !!useOptimizelyExperiment(OptimizelyClientSideExperiments.tripSearchPageSaveButton)

  return (
    <Main>
      <MainImageSegment>
        {offer.image.id &&
          <ResponsiveImage
            id={offer.image.id}
            gravity="north"
            role="presentation" />}
        {!hideAoLabel &&
          <ProductTypeLabelContainer>
            <ProductTypeLabel productType={offer.productType} />
          </ProductTypeLabelContainer>
        }
        {type !== OFFER_TYPE_LAST_MINUTE &&
          <OfferOverlayContainer className={cn({ hideTypeLabel: hideAoLabel })}>
            <OfferPartnershipLabel offer={offer} />
          </OfferOverlayContainer>
        }
        {badge && <StyledOfferBadge badge={badge} />}
        <TripGuard>
          <FloatingBookmarkContainer>
            <BookmarkButton offer={offer} iconOnly={isSaveButtonExperimentEnabled} />
          </FloatingBookmarkContainer>
        </TripGuard>
      </MainImageSegment>
      <Content>
        <LeftContent>
          <VerticalSpacer gap={4}>
            {property && showUserReviewsRating(property.rating, property.reviewsTotal) && (
              <NumberRating
                variant="medium"
                rating={property.rating}
                total={property.reviewsTotal}
                ratingSubtitle={property.reviewsSource === 'google' ? 'From online reviews' : ''}
              />
            )}
            <StyledBodyTextBlock variant="medium">
              <OfferTileLocationSection location={location} shouldShowMapLink={shouldShowMapButton} mapSearchUrl={mapSearchUrl} />
            </StyledBodyTextBlock>
            <OfferUrgencyLabels offer={offer} />
            {property?.name && <>
              <Heading variant="heading5" as="h4">{property.name}</Heading>
              <Breakpoint not="tablet">
                <BodyText variant="large" as="h4">{name}</BodyText>
              </Breakpoint>
            </>}
            {!property?.name && <BodyText variant="large" as="h5">{name}</BodyText>}
          </VerticalSpacer>
        </LeftContent>
        <RightContent>
          <SoldOutMessage offer={offer} filters={filters} />
        </RightContent>
      </Content>
    </Main>
  )
}

SearchOfferTileCondensed.defaultProps = {
  filters: {},
}

const mapStateToProps = (state: App.State) => ({
  windowSearch: state.router.location.search,
  shouldShowMapButton: getShowOnMapOfferExperimentVariant(state),
})

export default connect(mapStateToProps)(SearchOfferTileCondensed)
